import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/roles";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Brand } from "@/components/Brand";
import { FacilitySwitcher } from "@/components/FacilitySwitcher";

// A full-page shell for cross-gym views (Lead Nurture, Accounts): no left nav, just a slim top bar
// with "Back to FPA" and the same facility / view dropdown. `canSee(user)` gates the page.
export default function GlobalShell({ canSee, testid = "global", children }) {
  const { user, logout, effectiveRole } = useAuth();
  if (canSee && !canSee(user)) return <Navigate to="/" replace />;
  const initials = (user?.name || user?.email || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-[100dvh] flex flex-col bg-[#0a0a0a]" data-testid={`${testid}-shell`}>
      <header className="sticky top-0 z-20 glass border-b border-[#1f1f1f] px-3 sm:px-6 py-2.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <Link to="/" aria-label="FullPowerAnalytics home" className="shrink-0">
            <Brand size={30} showText={false} className="sm:hidden" />
            <Brand size={32} className="hidden sm:flex" />
          </Link>
          <Link to="/" data-testid="back-to-fpa"
            className="inline-flex items-center gap-1.5 rounded-full border border-[#262626] px-3 py-1.5 text-xs text-zinc-300 hover:text-white hover:border-zinc-500 whitespace-nowrap">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to FPA
          </Link>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <FacilitySwitcher />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid={`${testid}-user-menu`} className="rounded-full p-0.5 hover:bg-[#141414]" aria-label="Account">
                <Avatar className="h-8 w-8 border border-[#262626]">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-[#171717] text-[11px] text-zinc-300">{initials}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#0f0f0f] border-[#262626] text-zinc-200 w-56">
              <DropdownMenuLabel className="text-xs">
                <div className="text-zinc-200 font-medium truncate">{user?.name}</div>
                <div className="text-zinc-500 font-normal truncate">{user?.email}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-rose-400/80 mt-0.5">{ROLE_LABELS[effectiveRole || user?.role] || "User"}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-[#262626]" />
              <DropdownMenuItem onClick={logout} className="focus:bg-[#1a1a1a] cursor-pointer text-rose-300 focus:text-rose-300" data-testid={`${testid}-logout`}>
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      <main className="flex-1 min-w-0 px-3 sm:px-6 py-4 sm:py-5">{children}</main>
    </div>
  );
}
